import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { MemberRoleEnum } from 'src/enumerators';
import { environment } from 'src/environments/environment';
import { buildFallbackPermissions, fallbackSystemProfileFor } from '../rbac/rbac-fallback-policies';
import { RestApiService } from './rest-api.service';

import { Profile, ProfileDetail, PermissionsDto, RbacModule } from '../../models/rbac';

export type RbacAction = 'create' | 'read' | 'update' | 'delete';
export type RbacModuleKey = string;

export interface RbacMeResponse {
  workspaceId: string;
  permissions: Record<RbacModuleKey, RbacAction[]>;
  profiles?: string[];
}

export interface ProfileListItem {
  id: string;
  name: string;
}

export interface ProfileUsersGroupDto {
  profileId: string;
  name: string;
  key: string;
  isSystem: boolean;
  userIds: string[];
}

export interface ProfilesUsersResponse {
  profiles: ProfileUsersGroupDto[];
}

export type ProfileHistoryAction =
  | 'profile.create'
  | 'profile.update'
  | 'profile.rename'
  | 'profile.delete'
  | 'member.profiles.assign';

export interface ProfileHistoryItem {
  id: string;
  workspaceId: string;
  actorUserId?: string | null;
  actorName?: string | null;
  action: ProfileHistoryAction | string;
  targetProfileId?: string | null;
  targetProfileName?: string | null;
  targetUserId?: string | null;
  targetUserName?: string | null;
  before?: any;
  after?: any;
  createdAt: string;
}

export interface ProfileHistoryResponse {
  items: ProfileHistoryItem[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class RbacService extends RestApiService {
  private readonly _endpoint = environment.endpoint();

  // -----------------------------
  // ENDPOINTS (RBAC UI / CRUD)
  // -----------------------------
  private readonly _urlModules = '/rbac/modules';

  private readonly _urlProfiles = '/rbac/profiles';
  private readonly _urlProfileId = '/rbac/profiles/:id';
  private readonly _urlProfilePermissions = '/rbac/profiles/:id/permissions';

  private readonly _urlUserProfiles = '/rbac/users/:userId/profiles';

  private readonly _urlProfilesUsers = '/rbac/profiles/users';
  private readonly _urlProfileHistory = '/rbac/profiles/history';

  // -----------------------------
  // STATE (RBAC ME / cache)
  // -----------------------------
  private readonly _state$ = new BehaviorSubject<RbacMeResponse | null>(null);
  readonly state$ = this._state$.asObservable();

  private _inFlight$?: Observable<RbacMeResponse | null>;
  private _loadedWorkspaceId?: string;
  private _lastLoadedAt?: number;

  // TTL (5 min)
  private readonly TTL_MS = 5 * 60 * 1000;

  // TTL curto para o modo contingência. `ensureLoaded` é chamado no ngOnInit e em
  // todo `focus`/`visibilitychange` (app.component.ts), então deixar `_lastLoadedAt`
  // zerado faria cada alt-tab de cada aba virar um GET contra o backend que já está
  // falhando. Este piso retenta rápido sem transformar a indisponibilidade em carga.
  private readonly DEGRADED_TTL_MS = 30 * 1000;

  // -----------------------------
  // CONTINGÊNCIA (RBAC indisponível)
  // -----------------------------
  // Role do membro logado, alimentado pelo AuthService quando ele carrega o
  // membro atual. Injetar o AuthService aqui criaria dependência circular
  // (AuthService já injeta este serviço), por isso o valor é empurrado.
  private _fallbackRole: MemberRoleEnum | string | null = null;

  private readonly _degraded$ = new BehaviorSubject<boolean>(false);
  /** true enquanto as permissões vierem do role do membro, não de /rbac/me. */
  readonly degraded$ = this._degraded$.asObservable();

  constructor(public http: HttpClient) {
    super(http);
  }

  // -----------------------------
  // Helpers: Token / UserId
  // -----------------------------
  private getAccessTokenFromStorage(): string | null {
    return localStorage.getItem('JWT_TOKEN');
  }

  private decodeJwtPayload(token: string): any | null {
    try {
      const part = token.split('.')[1];
      if (!part) return null;

      const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '==='.slice((base64.length + 3) % 4);
      const json = decodeURIComponent(
        atob(padded)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  getCurrentUserId(): string | null {
    const token = this.getAccessTokenFromStorage();
    if (!token) return null;

    const payload = this.decodeJwtPayload(token);
    const sub = payload?.sub;
    return sub ? String(sub) : null;
  }

  // Tipo de licença embutido no JWT (`{ user, license: { type } }`, ver
  // backend jwt.infra-service.ts). Usado só na contingência, para aplicar o
  // mesmo recorte por licença que /rbac/me aplicaria.
  private getLicenseTypeFromToken(): string | null {
    const token = this.getAccessTokenFromStorage();
    if (!token) return null;

    const payload = this.decodeJwtPayload(token);
    const type = payload?.license?.type;
    return type ? String(type) : null;
  }

  // -----------------------------
  // Contingência
  // -----------------------------
  /**
   * Registra o role do membro logado, usado para montar as permissões quando
   * os endpoints de RBAC estiverem indisponíveis. Chamado pelo AuthService ao
   * carregar o membro atual, e limpado no logout.
   *
   * Se já estivermos em contingência, recalcula na hora — o role pode chegar
   * depois de uma tentativa que já falhou (ex.: `ensureLoaded` disparado pelo
   * AppComponent antes de o membro ter sido carregado).
   */
  setFallbackRole(role: MemberRoleEnum | string | null): void {
    if (this._fallbackRole === role) return;

    this._fallbackRole = role;
    if (this._degraded$.value) this.applyFallbackState();
  }

  /** true quando as permissões vigentes vieram do role, não de /rbac/me. */
  isDegraded(): boolean {
    return this._degraded$.value;
  }

  /**
   * Só cai para o role quando o RBAC está de fato **indisponível**: falha de
   * rede (status 0), endpoint ausente (404) ou erro de servidor (5xx).
   *
   * 401 e 403 são respostas deliberadas — sessão inválida ou acesso negado — e
   * não podem virar contingência, sob pena de a UI liberar o que o servidor
   * acabou de recusar. Nesses casos o comportamento antigo é mantido: sem
   * permissão nenhuma.
   */
  private isRbacUnavailable(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return true;
    if (error.status === 0) return true;
    if (error.status === 404) return true;
    return error.status >= 500;
  }

  /**
   * Forma mínima de uma resposta de `/rbac/me`: o que `can()` e `isOwner()` leem.
   * Não valida o conteúdo — só distingue a resposta da API de um corpo que não é
   * a resposta da API.
   */
  private isRbacMeResponse(res: unknown): res is RbacMeResponse {
    if (!res || typeof res !== 'object') return false;
    const permissions = (res as RbacMeResponse).permissions;
    return !!permissions && typeof permissions === 'object';
  }

  private applyFallbackState(): void {
    const permissions = buildFallbackPermissions(this._fallbackRole, this.getLicenseTypeFromToken());
    const payload = this.decodeJwtPayload(this.getAccessTokenFromStorage() ?? '');

    this._state$.next({
      workspaceId: payload?.user?.workspaceId ?? '',
      permissions,
      profiles: [fallbackSystemProfileFor(this._fallbackRole)],
    });
  }

  // -----------------------------
  // catálogo de módulos (UI)
  // -----------------------------
  listModules(): Observable<RbacModule[]> {
    const url = this._endpoint + this._urlModules;
    return this.http
      .get<{ modules: RbacModule[] }>(url)
      .pipe(map((r) => r?.modules || []));
  }

  // -----------------------------
  // perfis (CRUD)
  // -----------------------------
  listProfiles(): Observable<Profile[]> {
    const url = this._endpoint + this._urlProfiles;
    return this.http.get<Profile[]>(url);
  }

  getProfile(profileId: string): Observable<ProfileDetail> {
    const url = this._endpoint + this._urlProfileId.replace(':id', profileId);

    return this.http.get<any>(url).pipe(
      map(
        (d) =>
          ({
            ...d,
            permissions: (d?.permissionsJson ?? d?.permissions ?? {}) as PermissionsDto,
            permissionsJson: (d?.permissionsJson ?? undefined) as PermissionsDto | undefined,
          }) as ProfileDetail,
      ),
    );
  }

  createProfile(name: string, permissions: PermissionsDto): Observable<Profile> {
    const url = this._endpoint + this._urlProfiles;

    return this.http.post<Profile>(url, {
      name,
      permissions,
      permissionsJson: permissions,
      permissionJson: permissions,
    });
  }

  renameProfile(profileId: string, name: string): Observable<Profile> {
    const url = this._endpoint + this._urlProfileId.replace(':id', profileId);
    return this.http.patch<Profile>(url, { name });
  }

  setProfilePermissions(profileId: string, permissions: PermissionsDto): Observable<ProfileDetail> {
    const url = this._endpoint + this._urlProfilePermissions.replace(':id', profileId);

    return this.http
      .put<any>(url, {
        permissions,
        permissionsJson: permissions,
        permissionJson: permissions,
      })
      .pipe(
        map(
          (d) =>
            ({
              ...d,
              permissions: (d?.permissionsJson ?? d?.permissions ?? permissions ?? {}) as PermissionsDto,
              permissionsJson: (d?.permissionsJson ?? undefined) as PermissionsDto | undefined,
            }) as ProfileDetail,
        ),
      );
  }

  deleteProfile(profileId: string): Observable<{ ok: boolean }> {
    const url = this._endpoint + this._urlProfileId.replace(':id', profileId);
    return this.http.delete<{ ok: boolean }>(url);
  }

  // -----------------------------
  // atribuição de perfis ao usuário
  // -----------------------------
  getUserProfiles(userId: string): Observable<{ roles: string[] }> {
    const url = this._endpoint + this._urlUserProfiles.replace(':userId', userId);
    return this.http.get<{ roles: string[] }>(url);
  }

  setUserProfiles(userId: string, profileIds: string[]): Observable<{ ok: boolean }> {
    const url = this._endpoint + this._urlUserProfiles.replace(':userId', userId);
    return this.http.put<{ ok: boolean }>(url, { profileIds });
  }

  listProfilesUsers(): Observable<ProfilesUsersResponse> {
    const url = this._endpoint + this._urlProfilesUsers;
    return this.http
      .get<ProfilesUsersResponse>(url)
      .pipe(map((r) => r ?? { profiles: [] }));
  }

  // histórico de alterações de perfis (somente Proprietário no backend)
  listProfileHistory(opts?: { limit?: number; offset?: number }): Observable<ProfileHistoryResponse> {
    let url = this._endpoint + this._urlProfileHistory;
    const params: string[] = [];
    if (opts?.limit != null) params.push(`limit=${opts.limit}`);
    if (opts?.offset != null) params.push(`offset=${opts.offset}`);
    if (params.length) url += '?' + params.join('&');
    return this.http
      .get<ProfileHistoryResponse>(url)
      .pipe(map((r) => r ?? { items: [], total: 0 }));
  }

  listMyProfileIds(): Observable<string[]> {
    const userId = this.getCurrentUserId();
    if (!userId) return of([]);

    
    return this.listProfilesUsers().pipe(
      map((res) => res?.profiles || []),
      map((profiles) =>
        profiles
          .filter((p) => Array.isArray(p.userIds) && p.userIds.includes(userId))
          .map((p) => String(p.profileId)),
      ),
      catchError(() => of([])),
    );
  }

  // -----------------------------
  // permissões efetivas do usuário (/rbac/me)
  // -----------------------------
  ensureLoaded(expectedWorkspaceId?: string): Observable<RbacMeResponse | null> {
    const state = this._state$.value;

    const ttlOk = this._lastLoadedAt && Date.now() - this._lastLoadedAt < this.TTL_MS;
    const workspaceOk =
      !expectedWorkspaceId ||
      this._loadedWorkspaceId === expectedWorkspaceId ||
      state?.workspaceId === expectedWorkspaceId;

    if (state && ttlOk && workspaceOk) return of(state);
    return this.refreshMe();
  }

  refreshMe(): Observable<RbacMeResponse | null> {
    if (this._inFlight$) return this._inFlight$;

    const url = this._endpoint + '/rbac/me';

    this._inFlight$ = this.http.get<RbacMeResponse>(url).pipe(
      tap((res) => {
        // 200 com corpo malformado (proxy/CDN/gateway devolvendo HTML de erro numa
        // rota de API) não é classificado por `isRbacUnavailable`, que olha só o
        // status. Sem esta validação o corpo inválido virava estado, armava o TTL e
        // `can()` devolvia false para tudo — a casca vazia que esta contingência
        // existe para evitar, e sem sequer degradar. O throw cai no catchError
        // abaixo, e `isRbacUnavailable` devolve true para erro que não é
        // HttpErrorResponse, então a contingência entra normalmente.
        if (!this.isRbacMeResponse(res)) throw new Error('resposta de /rbac/me inválida');

        this._degraded$.next(false);
        this._state$.next(res);
        this._loadedWorkspaceId = res?.workspaceId;
        this._lastLoadedAt = Date.now();
        this._inFlight$ = undefined;
      }),
      catchError((error) => {
        this._loadedWorkspaceId = undefined;
        this._inFlight$ = undefined;

        // Sem contingência: mantém o comportamento anterior (nenhuma permissão).
        if (!this.isRbacUnavailable(error)) {
          this._degraded$.next(false);
          this._state$.next(null);
          this._lastLoadedAt = undefined;
          return of(null);
        }

        // RBAC indisponível: opera com as permissões do role do membro para o
        // software seguir utilizável. `_lastLoadedAt` fica zerado de propósito,
        // para que a próxima chamada a `ensureLoaded` tente o endpoint de novo
        // em vez de servir a contingência pelo TTL.
        console.warn(
          '[RBAC] /rbac/me indisponível — usando as permissões do role do membro (modo contingência).',
          error,
        );
        this._lastLoadedAt = Date.now() - (this.TTL_MS - this.DEGRADED_TTL_MS);
        this._degraded$.next(true);

        // Só deriva do role quando não há nada carregado. Se `/rbac/me` já respondeu
        // nesta sessão, o estado em memória é a resposta real do membro — apenas
        // vencida pelo TTL — e é estritamente melhor que a tabela derivada, que é o
        // perfil de SISTEMA do role e ignora perfil customizado. Sobrescrever daria
        // menu a mais (com 403 a cada clique) ou a menos, tendo a resposta certa em
        // mãos. O cenário que o PR persegue — primeira carga com o RBAC fora — tem
        // `_state$` nulo e entra na contingência igual.
        if (!this._state$.value) this.applyFallbackState();

        return of(this._state$.value);
      }),
      shareReplay(1),
    );

    return this._inFlight$;
  }

  invalidate(): void {
    this._state$.next(null);
    this._degraded$.next(false);
    this._loadedWorkspaceId = undefined;
    this._lastLoadedAt = undefined;
    this._inFlight$ = undefined;
  }

  can(module: RbacModuleKey, action: RbacAction): boolean {
    const perms = this._state$.value?.permissions;
    const actions = perms?.[module];
    return Array.isArray(actions) && actions.includes(action);
  }

  // true se o usuário logado pertence ao perfil de sistema Proprietário (Owner).
  // Em contingência, `profiles` carrega o perfil equivalente ao role do membro
  // (ver applyFallbackState), então a checagem continua valendo.
  isOwner(): boolean {
    return (this._state$.value?.profiles ?? []).includes('SYSTEM:OWNER');
  }

  can$(module: RbacModuleKey, action: RbacAction): Observable<boolean> {
    return this.state$.pipe(map(() => this.can(module, action)));
  }
}